import logger from '../../../utils/logger';

/**
 * Regex recognizing the set of YouTube URL shapes we accept from pasted
 * input. Matches bare `youtube.com`, `m.youtube.com`, `music.youtube.com`,
 * `youtu.be`, and variants with/without protocol/www. Anything beyond the
 * domain must have at least one further character so we don't treat
 * `https://youtube.com` (with no path) as a valid song URL.
 */
export const YOUTUBE_URL_REGEX =
  /^(https?:\/\/)?(www\.|m\.)?(youtube\.com|music\.youtube\.com|youtu\.be)\/.+/;

/**
 * Best-effort song name / artist extraction from a video's channel+title.
 *
 *   - `<Artist> - Topic` auto-generated channels: channel name IS the
 *     artist, full video title is the song name.
 *   - Otherwise we try to parse "Artist - Song" (any dash variant) from
 *     the video title.
 *   - Failing both, we fall back to dumping the full title into
 *     `songName` and leave `songArtist` blank for the user to fill in.
 */
export const extractSongDetails = (channelTitle, videoTitle) => {
  if (channelTitle.endsWith('- Topic')) {
    const artistName = channelTitle.slice(0, -7);
    return {
      songName: videoTitle,
      songArtist: artistName,
    };
  }
  const titlePattern = /^(.+?)\s*[–—-]\s*(.+)$/;
  const match = videoTitle.match(titlePattern);
  if (match) {
    return {
      songName: match[2].trim(),
      songArtist: match[1].trim(),
    };
  }
  return {
    songName: videoTitle,
    songArtist: '',
  };
};

/**
 * Extract the 11-char video id from any supported YouTube URL. Tries
 * multiple patterns in priority order and only returns an id that passes
 * the canonical 11-char alphanumeric/dash/underscore shape check (guards
 * against malformed matches that happen to contain an early query param).
 *
 * Returns `null` when no valid id can be found.
 */
export const extractVideoId = (url) => {
  if (!url) return null;

  if (url.includes('music.youtube.com')) {
    const musicMatch = url.match(/[?&]v=([^&]+)/);
    if (musicMatch) return musicMatch[1];
  }

  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /youtube\.com\/v\/([^&\n?#]+)/,
    /youtube\.com\/watch\?.*v=([^&\n?#]+)/,
    /youtu\.be\/([^&\n?#]+)/,
    /youtube\.com\/embed\/([^&\n?#]+)/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      const videoId = match[1];
      if (/^[a-zA-Z0-9_-]{11}$/.test(videoId)) {
        return videoId;
      }
    }
  }

  return null;
};

/**
 * Normalize a `music.youtube.com` URL into its `www.youtube.com`
 * equivalent, stripping everything except the `v=` param. Used so the
 * persisted `URL` field is always the canonical watch URL regardless of
 * which YouTube surface the user pasted from.
 *
 * Returns the original string when parsing fails (defensive).
 */
export const normalizeYouTubeURL = (url) => {
  try {
    const parsedURL = new URL(url);
    if (parsedURL.hostname === 'music.youtube.com') {
      parsedURL.hostname = 'www.youtube.com';
    }
    const videoId = parsedURL.searchParams.get('v');
    parsedURL.search = videoId ? `?v=${videoId}` : '';
    return parsedURL.toString();
  } catch (error) {
    logger.warn('Error normalizing YouTube URL:', error);
    return url;
  }
};

/**
 * Fetch a video's snippet (title, thumbnails, channelTitle) from the
 * YouTube Data API. Returns `null` (not throws) for any of:
 *   - missing/invalid API key
 *   - non-OK HTTP response
 *   - empty `items` array
 *   - missing required fields
 *   - missing thumbnails (we rely on them for the post preview)
 *
 * The caller is expected to gate UI state on a non-null return value.
 */
export const fetchVideoDetails = async (videoId) => {
  try {
    const API_KEY = process.env.REACT_APP_YTAPI;

    if (!API_KEY) {
      logger.warn('Missing REACT_APP_YTAPI key');
      return null;
    }

    const fetchUrl = `https://www.googleapis.com/youtube/v3/videos?part=snippet&id=${videoId}&key=${API_KEY}`;
    logger.debug('YT Fetch URL:', fetchUrl);

    const response = await fetch(fetchUrl);
    if (!response.ok) {
      throw new Error(`YouTube API responded with status: ${response.status}`);
    }

    const data = await response.json();

    if (!data || !Array.isArray(data.items) || data.items.length === 0) {
      logger.warn(
        `No video data returned from YouTube API for video ID: ${videoId}`
      );
      return null;
    }

    const { title, thumbnails, channelTitle } = data.items[0].snippet;

    if (!title || !channelTitle) {
      logger.warn(`Missing title or channel title for video ID: ${videoId}`);
      return null;
    }

    if (!thumbnails || Object.keys(thumbnails).length === 0) {
      logger.warn(
        `No thumbnails returned from YouTube API for video: ${videoId}`
      );
      return null;
    }

    const availableThumbnails = Object.keys(thumbnails);
    if (availableThumbnails.length === 0) {
      logger.warn(`No thumbnail qualities available for video ID: ${videoId}`);
      return null;
    }

    logger.debug(
      `Successfully fetched video details for ID: ${videoId}, available thumbnails:`,
      availableThumbnails
    );
    return { snippet: { title, thumbnails, channelTitle } };
  } catch (error) {
    logger.error(`Error fetching video details for ID ${videoId}:`, error);
    return null;
  }
};
